import { useEffect, useRef, useState } from 'react';
import { runtimeMode, type OemContext } from '../../lib/auth';
import { retainOnlineCommand, settleOnlineCommand } from '../../lib/oem-pending';
import {
  acknowledgeHandover,
  loadHandoverAcknowledgements,
  recoverReceipt,
  OemApiError,
  type HandoverAcknowledgements,
} from '../../lib/oem-client';
export function HandoverAcknowledgement({
  context,
  orderId,
  online,
  userId,
}: {
  readonly context: OemContext;
  readonly orderId: string;
  readonly online: boolean;
  readonly userId: string;
}) {
  const [data, setData] = useState<HandoverAcknowledgements | null>(null),
    [comment, setComment] = useState(''),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [message, setMessage] = useState('');
  const pending = useRef<Parameters<typeof acknowledgeHandover>[0] | null>(null);
  const currentData =
    online && data?.orderId === orderId && data.projectId === context.projectId ? data : null;
  useEffect(() => {
    let active = true;
    if (runtimeMode === 'connected' && online)
      void loadHandoverAcknowledgements(orderId)
        .then((value) => {
          if (active) setData(value);
        })
        .catch((e: unknown) => {
          if (active) setMessage(e instanceof Error ? e.message : 'unavailable');
        });
    return () => {
      active = false;
    };
  }, [orderId, context.projectId, online]);
  async function submit() {
    if (!currentData || !online || !context.actions.includes('acknowledge_handover')) return;
    setBusy(true);
    setMessage('');
    try {
      const previous = pending.current;
      const body = previous ?? {
        projectId: context.projectId,
        orderId,
        orderVersion: currentData.orderVersion,
        orderHash: currentData.orderHash,
        idempotencyKey: crypto.randomUUID(),
        comment,
      };
      pending.current = body;
      setUncertain(true);
      await retainOnlineCommand(context, userId, 'acknowledge_handover', {
        idempotencyKey: body.idempotencyKey,
        projectId: body.projectId,
        skuId: null,
        target: null,
        payload: body,
      });
      let result: unknown;
      if (previous) {
        try {
          result = await recoverReceipt(context, body.idempotencyKey);
        } catch (e) {
          if (!(e instanceof OemApiError && e.status === 404)) throw e;
        }
      }
      result ??= await acknowledgeHandover(body);
      if (
        typeof result !== 'object' ||
        !result ||
        !('acknowledgementId' in result) ||
        !('orderId' in result) ||
        result.orderId !== orderId
      )
        throw new Error('receipt_identity_unverified');
      pending.current = null;
      setUncertain(false);
      await settleOnlineCommand(body.idempotencyKey);
      setComment('');
      setMessage(
        'รับทราบแล้ว · server receipt ' + ('receiptId' in result ? String(result.receiptId) : ''),
      );
      try {
        setData(await loadHandoverAcknowledgements(orderId));
      } catch {
        setMessage(
          'รับทราบแล้ว · server receipt ' +
            ('receiptId' in result ? String(result.receiptId) : '') +
            ' · โหลดประวัติไม่ได้',
        );
      }
    } catch (e) {
      if (e instanceof OemApiError && e.status < 500) {
        if (pending.current) await settleOnlineCommand(pending.current.idempotencyKey);
        pending.current = null;
        setUncertain(false);
      }
      setMessage(e instanceof Error ? e.message : 'ยังไม่ทราบผล · ตรวจ server receipt ก่อนส่งซ้ำ');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="รับทราบความต้องการส่งต่อ">
      <h3>ความต้องการส่งต่อบน baseline ที่ตรึง</h3>
      <p>การรับทราบไม่ใช่การอนุญาตจัดซื้อ ผลิต หรือปล่อยสินค้า</p>
      {currentData?.entries.map((entry) => (
        <p key={entry.id}>
          {entry.comment}
          <br />
          <small>
            {entry.actorId} · {entry.recordedAt} · receipt {entry.receiptId}
          </small>
        </p>
      ))}
      {context.actions.includes('acknowledge_handover') ? (
        <>
          <label>
            ความต้องการและข้อสังเกตปลายทาง
            <textarea
              value={comment}
              disabled={busy || uncertain || !online}
              onChange={(event) => setComment(event.target.value)}
              maxLength={4000}
            />
          </label>
          <button
            className="action"
            disabled={!online || !currentData || busy || !comment.trim()}
            onClick={() => void submit()}
          >
            รับทราบ baseline นี้
          </button>
        </>
      ) : (
        <p className="muted">สิทธิ์อ่านหลักฐาน · ไม่สามารถรับทราบแทนผู้รับผิดชอบ</p>
      )}
      {message ? (
        <p role="status" className="wrap">
          {message}
        </p>
      ) : null}
      {!online ? <p className="note-warning">ต้องเชื่อมต่อและตรวจสิทธิ์ก่อนรับทราบ</p> : null}
    </section>
  );
}
